import { apiClient } from "./apiClient.js";
import Livekit from "./livekit.js";

class Chat {
    constructor() {
        this.attemptId = null;
        this.sessionId = null;
        this.userId = null;
        this.role = null;
        this.callbacks = {
            onMessage: null,
            onError: null,
        };
    }

    /**
     * Initialize the chat system
     * @param {Object} options Configuration options
     * @param {string} options.attemptId The exam attempt ID (required)
     * @param {string} options.sessionId The exam session ID (optional, for backward compatibility)
     * @param {string} options.userId The user ID (required)
     * @param {string} options.role 'user' or 'invigilator'
     * @param {Object} options.callbacks Event callbacks
     */
    init(options = {}) {
        this.attemptId = options.attemptId;
        this.sessionId = options.sessionId;
        this.userId = options.userId;
        this.role = options.role || "user";

        if (options.callbacks) {
            this.callbacks = { ...this.callbacks, ...options.callbacks };
        }

        // Listen for LiveKit chat messages
        Livekit.callbacks.on_chat_message = (data) => {
            const message = {
                id: crypto.randomUUID(),
                message: data.message,
                timestamp: data.timestamp,
                sender: data.senderRole === "user" ? "me" : "invigilator",
                senderRole: data.senderRole,
                senderId: data.senderId,
                attemptId: data.attemptId,
            };
            this.callbacks.onMessage?.(message);
        };
    }

    /**
     * Send a message
     * @param {string} message Text message content
     */
    async sendMessage(message) {
        if (!this.attemptId || !this.userId) {
            throw new Error("Chat not initialized (missing attemptId or userId)");
        }

        try {
            const response = await apiClient.post("/client/chat/send", {
                message: message,
            });

            if (response.success) {
                return response.message;
            } else {
                throw new Error(response.error || "Failed to send message");
            }
        } catch (error) {
            console.error("[Chat] Send failed:", error);
            this.callbacks.onError?.(error);
            throw error;
        }
    }

    /**
     * Fetch chat history
     * @param {number} limit Number of messages to fetch
     * @param {number} offset Offset for pagination
     */
    async getHistory(limit = 50, offset = 0) {
        if (!this.attemptId) {
            throw new Error("Chat not initialized (missing attemptId)");
        }

        try {
            const response = await apiClient.get(
                `/client/chat/attempt/${this.attemptId}?limit=${limit}&offset=${offset}`
            );

            if (response.success) {
                return response.messages;
            } else {
                throw new Error(response.error || "Failed to fetch history");
            }
        } catch (error) {
            console.error("[Chat] History fetch failed:", error);
            this.callbacks.onError?.(error);
            throw error;
        }
    }


}

export default new Chat();
